import {
  IAccountRespSchema,
  IContactRespSchema,
  LegalEntityAddressFormatEnum,
} from '@monetize/types/app';
import { forwardRef, ReactNode, useState } from 'react';
import { ContactAddress } from '../../../../../components/Contacts/ContactAddress';
import {
  MBox,
  MButton,
  MFlex,
  MFormField,
  MHStack,
} from '../../../../../components/Monetize';
import { MainSearchInput } from '../../../../../components/Monetize/MCustomSelect/components/MainSearchInput';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { QuoteBasicContactSelect } from '../../../../../components/Quotes/QuoteBasicContactSelect';
import { renderItemContentForAccount } from '../../../../Settings/rules-v3/rules.utils';
import { QuoteContactForm } from './QuoteContactForm';

export interface QuoteContactDrawerSelectProps {
  isDisabled?: boolean;
  isReadOnly?: boolean;

  label: string;
  /**
   * When provided, the underlying contact select uses this list (client-side).
   * When omitted, it fetches paginated contacts from `/api/accounts/{accountId}/contacts`.
   */
  contacts?: IContactRespSchema[];
  value?: IContactRespSchema | null;
  accountId: string;
  onChange: (contact: IContactRespSchema) => void;

  isInternal?: boolean;
  children?: ReactNode;
  addressFormat?: LegalEntityAddressFormatEnum | null;
  canUpdateContact: boolean;
  showAddress?: boolean;

  renderPreContent?: () => ReactNode;
  handleChangeAccount?: (accountId: string | null) => void;
  accountSelectLabel?: string;
  accountSelectItems?: IAccountRespSchema[];
  showAccountSelect?: boolean;
  isAccountSelectDisabled?: boolean;
  accountSelectTooltip?: string;
}

export const QuoteContactDrawerSelect = forwardRef<
  any,
  QuoteContactDrawerSelectProps
>(
  (
    {
      isDisabled,
      isReadOnly,

      label,
      contacts,
      value: contact,
      accountId,
      onChange,

      isInternal = false,
      children,
      addressFormat,
      canUpdateContact,
      showAddress = true,
      renderPreContent,
      handleChangeAccount,
      accountSelectLabel,
      accountSelectItems,
      showAccountSelect,
      isAccountSelectDisabled,
      accountSelectTooltip,
    }: QuoteContactDrawerSelectProps,
    ref,
  ) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [isNewContact, setIsNewContact] = useState(false);

    const omitProperties: (keyof IContactRespSchema)[] = showAddress
      ? ['fullName']
      : ['fullName', 'address'];

    function handleCloseEdit(updatedContact?: IContactRespSchema) {
      setIsEditMode(false);
      if (updatedContact) {
        onChange(updatedContact);
      }
    }

    return (
      <MFlex direction="column" grow={1} w="full">
        <MHStack>
          {showAccountSelect && (
            <MFormField
              label={accountSelectLabel}
              flex="1"
              tooltip={accountSelectTooltip}
            >
              <MCustomSelect
                itemTitle="accountName"
                itemValue="id"
                items={accountSelectItems}
                useMainInputAsSearchInput
                MainInputComponent={MainSearchInput}
                renderItemContent={renderItemContentForAccount}
                value={accountId}
                onChange={handleChangeAccount}
                isSelectOpen={false}
                isDisabled={isAccountSelectDisabled || isEditMode}
                isReadOnly={isAccountSelectDisabled || isReadOnly}
              />
            </MFormField>
          )}

          <MBox flex="1">
            <MFlex justifyContent="space-between" alignItems="center" flex="1">
              <MFormField label={label} />
              {!isDisabled &&
                !isEditMode &&
                (!isInternal || contact?.accountId) &&
                canUpdateContact &&
                contact && (
                  <MBox>
                    <MButton
                      size="xs"
                      ml={-2}
                      mt={-1}
                      p={2}
                      variant="tertiary"
                      onClick={() => {
                        setIsEditMode(true);
                        setIsNewContact(false);
                      }}
                    >
                      Edit
                    </MButton>
                  </MBox>
                )}
            </MFlex>
            <QuoteBasicContactSelect
              value={contact}
              onChange={onChange}
              contacts={contacts}
              accountId={accountId}
              isDisabled={isDisabled || isEditMode}
              isReadOnly={isReadOnly}
              useMainInputAsSearchInput
              MainInputComponent={MainSearchInput}
              showContentInInput={false}
              placeholder="Select Contact"
              popOverProps={{ positioning: { sameWidth: true } }}
              handleOnNewContact={() => {
                setIsEditMode(true);
                setIsNewContact(true);
              }}
              allowContactCreation={true}
              renderPreContent={renderPreContent}
            />
          </MBox>
        </MHStack>

        {isEditMode && (
          <QuoteContactForm
            accountId={accountId}
            isInternal={isInternal}
            formId={`contact-form-${contact?.id || 'new'}`}
            existingContact={
              isNewContact
                ? isInternal
                  ? { internal: true, esigner: true }
                  : {}
                : contact
            }
            onClose={handleCloseEdit}
            mt="2"
          />
        )}

        {!isEditMode && contact && (
          <MFlex
            justifyContent="spread"
            alignItems="flex-start"
            mt={isReadOnly ? 0 : 2}
          >
            <MFlex direction="column" grow={1}>
              <ContactAddress
                textProps={{ color: 'tGray.darkPurple' }}
                contact={contact}
                omitProperties={omitProperties}
                addressFormat={addressFormat}
              />
            </MFlex>
          </MFlex>
        )}
      </MFlex>
    );
  },
);
